import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

interface ThemeContextValue {
  /** User's preference: 'light', 'dark', or 'system' */
  preference: ThemePreference
  /** Actual applied theme after resolving 'system' to light/dark */
  resolvedTheme: ResolvedTheme
  /** Set the theme preference */
  setTheme: (theme: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'theme-preference'

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored
  }
  // Migration: check old 'theme' key from previous implementation
  const oldTheme = localStorage.getItem('theme')
  if (oldTheme === 'light' || oldTheme === 'dark') {
    // Migrate to new key
    localStorage.setItem(STORAGE_KEY, oldTheme)
    localStorage.removeItem('theme')
    return oldTheme
  }
  return 'system'
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(resolved)
  root.style.colorScheme = resolved
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>(getStoredPreference)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    const pref = getStoredPreference()
    if (pref === 'system') return getSystemTheme()
    return pref
  })

  // Handle system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent) => {
      if (preference === 'system') {
        const newTheme = e.matches ? 'dark' : 'light'
        setResolvedTheme(newTheme)
        applyTheme(newTheme)
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [preference])

  // Apply theme when preference changes
  useEffect(() => {
    const resolved = preference === 'system' ? getSystemTheme() : preference
    setResolvedTheme(resolved)
    applyTheme(resolved)
    localStorage.setItem(STORAGE_KEY, preference)
  }, [preference])

  const setTheme = useCallback((theme: ThemePreference) => {
    setPreference(theme)
  }, [])

  return (
    <ThemeContext.Provider value={{ preference, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useThemeContext() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeProvider')
  }
  return context
}

/**
 * Like useThemeContext but tolerant of a missing provider — returns
 * a sensible default ('light' for the resolved theme, 'system' for
 * the preference, no-op setter). Use from leaf components / hooks
 * that may render in test contexts without the full app shell, where
 * a missing provider should not blow up the tree.
 */
export function useThemeContextOptional(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (context) return context
  return {
    preference: 'system',
    resolvedTheme: 'light',
    setTheme: () => {},
  }
}
